import React, { useEffect, useState } from 'react'
import { isNativeShell, nativeShellVersion } from '../lib/nativeShell'
import {
    snapshotViewport,
    subscribeViewportLog,
    viewportLog,
    type ViewportSnapshot,
} from '../lib/viewportLog'

// The fields shown, in order. `at`, `cause` and `path` head each log entry.
const FIELDS: (keyof ViewportSnapshot)[] = [
    'safeTop', 'navTop', 'navH', 'scrollY',
    'innerH', 'clientH', 'vvH', 'vvOffsetTop', 'vvPageTop', 'vvScale',
    'innerW', 'screenH', 'safeBottom', 'visibility', 'focused',
]

const time = (ms: number) =>
    new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })

/**
 * Admin: the viewport's geometry now, and every change to it since the app
 * opened (lib/viewportLog). For catching the iOS shell's header drifting down
 * by a status bar's height: open this while it is happening.
 */
const ViewportDebugPage: React.FC = () => {
    const [now, setNow] = useState(() => snapshotViewport('now'))
    const [, setTick] = useState(0)
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        const id = window.setInterval(() => setNow(snapshotViewport('now')), 500)
        const off = subscribeViewportLog(() => setTick((t) => t + 1))
        return () => {
            window.clearInterval(id)
            off()
        }
    }, [])

    const entries = [...viewportLog()].reverse()

    const copy = async () => {
        const text = JSON.stringify(
            { shell: isNativeShell(), version: nativeShellVersion(), ua: navigator.userAgent, now, log: viewportLog() },
            null,
            1,
        )
        try {
            await navigator.clipboard.writeText(text)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1500)
        } catch {
            window.prompt('Copy this', text)
        }
    }

    return (
        <div className="container mx-auto px-4 pb-8 sm:pt-8 text-sm">
            <div className="flex items-center justify-between gap-3 mb-3">
                <h1 className="text-2xl font-bold text-gray-800">Viewport</h1>
                <button
                    type="button"
                    onClick={() => void copy()}
                    className="px-3 py-1.5 rounded-lg bg-secondary hover:bg-secondary-dark text-gray-700"
                >
                    {copied ? 'Copied' : 'Copy log'}
                </button>
            </div>
            <p className="text-gray-500 mb-3">
                {isNativeShell() ? `iOS app ${nativeShellVersion() ?? ''}` : 'Browser'} · {entries.length} changes
            </p>

            <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
                <h2 className="font-semibold text-gray-800 mb-2">Now</h2>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono text-xs">
                    {FIELDS.map((f) => (
                        <React.Fragment key={f}>
                            <dt className="text-gray-500">{f}</dt>
                            <dd className="text-gray-900 break-all">{String(now[f] ?? '–')}</dd>
                        </React.Fragment>
                    ))}
                </dl>
            </div>

            <h2 className="font-semibold text-gray-800 mb-2">Changes, newest first</h2>
            <ol className="space-y-2">
                {entries.map((e, i) => {
                    const prev = entries[i + 1]
                    const changed = prev ? FIELDS.filter((f) => e[f] !== prev[f]) : FIELDS
                    return (
                        <li key={`${e.at}-${i}`} className="bg-white rounded-lg border border-gray-200 p-2 font-mono text-xs">
                            <div className="text-gray-500 mb-1 break-all">
                                {time(e.at)} · <span className="text-gray-900">{e.cause}</span> · {e.path}
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                                {changed.map((f) => (
                                    <span key={f}>
                                        <span className="text-gray-500">{f}</span>{' '}
                                        {prev && <span className="text-gray-400 line-through">{String(prev[f] ?? '–')}</span>}{' '}
                                        <span className="text-gray-900">{String(e[f] ?? '–')}</span>
                                    </span>
                                ))}
                            </div>
                        </li>
                    )
                })}
            </ol>
        </div>
    )
}

export default ViewportDebugPage
